/**
 * Minimal typed `localStorage` wrapper.
 *
 * Every access is wrapped: Safari in private mode throws `QuotaExceededError`
 * on `setItem`, and some in-app browsers block storage entirely. The game must
 * keep running in that case — it simply forgets things between reloads.
 */

/** Every key written by this app is namespaced with this prefix. */
export const STORAGE_PREFIX = 'onitsoft.trucking.'

function store(): Storage | null {
  try {
    return typeof localStorage === 'undefined' ? null : localStorage
  } catch {
    return null
  }
}

function fullKey(key: string): string {
  return key.startsWith(STORAGE_PREFIX) ? key : STORAGE_PREFIX + key
}

/** Reads and parses a JSON value, returning `fallback` on any problem. */
export function readJson<T>(key: string, fallback: T): T {
  const s = store()
  if (!s) return fallback
  try {
    const raw = s.getItem(fullKey(key))
    if (raw === null) return fallback
    const parsed: unknown = JSON.parse(raw)
    return parsed === null || parsed === undefined ? fallback : (parsed as T)
  } catch {
    return fallback
  }
}

/** Serialises and stores a value. Silently does nothing when storage is unavailable. */
export function writeJson(key: string, value: unknown): void {
  const s = store()
  if (!s) return
  try {
    s.setItem(fullKey(key), JSON.stringify(value))
  } catch {
    /* storage full or blocked — the run still counts, it just is not persisted */
  }
}

/** Removes a key. Never throws. */
export function remove(key: string): void {
  const s = store()
  if (!s) return
  try {
    s.removeItem(fullKey(key))
  } catch {
    /* nothing to do */
  }
}
